import { Button } from '@codegouvfr/react-dsfr/Button';
import { useTextes } from '@common/config';
import { useCurrentCampagne } from "@common/hooks/campagne";
import { SIREN_REGEX } from '@common/schemas';
import { OrganismeStatutType } from '@common/services';
import { Form, TextField } from '@common/ui';
import React, { JSX, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useInscriptionState } from '../../hooks/use-inscription-state.hook';
import { STEP_INFO_MAP } from '../stepper/hooks/use-stepper-info.hook';
import { StepperInfo } from '../stepper/types/stepper-info.type';
import { useLoadNdas } from './hooks/use-load-ndas.hook';
import { usePrefillInscription } from './hooks/use-prefill-inscription.hook';
import { useSearchOrganismeBySiren } from './hooks/use-search-organisme-by-siren.hook';
import { useSirenMessages } from './hooks/use-siren-messages.hook';
import { SearchBySirenResult, searchBySirenSchema } from './schemas/search-by-siren.schema';
import './searchBySiren.css';

export const SearchBySiren = () => {
  const t = useTextes('Inscription', 'RenseignementSiren');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { organisme, requested } = useInscriptionState();
  const { campagne } = useCurrentCampagne();

  const { getOrganismeBySiren } = useSearchOrganismeBySiren();
  const { getInfoMessage, getBlockingMessage } = useSirenMessages();
  const { loadNdas } = useLoadNdas();
  const { prefillInscription } = usePrefillInscription();

  const [siren, setSiren] = useState<string>(organisme?.siren || searchParams.get('siren') || '');
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [blockingMessage, setBlockingMessage] = useState<JSX.Element | string | null>(null);

  const appartenaitCampagnePrecedente = !!organisme?.campagne?.annee
      && !!campagne?.annee
      && organisme.campagne.annee < campagne.annee;

  useEffect(() => {
    setBlockingMessage(null);
    setInfoMessage(null);

    if (!SIREN_REGEX.test(siren)) return setBlockingMessage(t['SIREN_MES1']);

    if (organisme?.siren === siren) {
      setInfoMessage(getInfoMessage());
      return;
    }

    getOrganismeBySiren(siren).then();
  }, [siren]);

  useEffect(() => {
    if (!requested) return setBlockingMessage(null);
    if (!organisme) return setBlockingMessage(t['SIREN_MES2']);

    setInfoMessage(getInfoMessage());
    setBlockingMessage(getBlockingMessage());
  }, [requested]);

  useEffect(() => {
    if (!searchParams.has('stepRedirect')) return;
    if (blockingMessage) return;
    if (!requested || !organisme) return;
    if (organisme.statut !== OrganismeStatutType.DEMANDE_INFORMATION && !appartenaitCampagnePrecedente) return;

    const stepRedirect: string | null = searchParams.get('stepRedirect');
    if (stepRedirect == null) return setSearchParams({});

    const stepperInfo: StepperInfo | undefined = STEP_INFO_MAP[stepRedirect];
    if (!stepperInfo) return setSearchParams({});

    loadAndNavigate(stepperInfo.route).then();
  }, [searchParams, blockingMessage, requested, organisme, appartenaitCampagnePrecedente]);

  const loadData = async () => {
    await loadNdas(siren);

    if (!organisme?.id) return;

    if (organisme.statut === OrganismeStatutType.DEMANDE_INFORMATION) {
      await prefillInscription(organisme.id);
      return;
    }

    if (appartenaitCampagnePrecedente) {
      try {
        await prefillInscription(organisme.id);
      } catch {
        // pas bloquant on continue sans prefill
      }
    }
  };

  const loadAndNavigate = async (route: string) => {
    try {
      await loadData();
      navigate(route);
    } catch (err: unknown) {
      if (err === 'CANNOT_PREFILL') setBlockingMessage(t['SIREN_TEXT_CANNOT_PREFILL']);
    }
  };

  const handleSubmit = () => loadAndNavigate('/inscription/etape-1');

  return (
    <div className={'containerStyle'}>

      <div className={'squareStyle'}>

        <h2>Plateforme de saisie des déclarations</h2>
        <br />
        <br />

        <div className={'my-5'}>{t['SIREN_TEXT2']}</div>
        <br />
        <div className={'my-5 text-sm'}>Les champs marqués d’un * sont obligatoires</div>

        <Form<SearchBySirenResult> onSubmit={handleSubmit}
                                   schema={searchBySirenSchema}
                                   defaultValues={{ siren }}>

          <TextField name="siren"
                     title="Numéro de SIREN"
                     placeholder="Ex: 426677124"
                     onChange={(ev) => setSiren(ev.target.value)}>

            <div className="mt-2 mb-4 flex flex-col gap-1 italic">
              {(infoMessage) && <span>{infoMessage}</span>}
              {(blockingMessage) && <span className={'text-red-500'}>{blockingMessage}</span>}
            </div>
          </TextField>

          <div className="separator mb-7"></div>

          <div className={'buttonsStyle'}>
            <Button disabled={!!blockingMessage || !siren} type="submit"><span>VALIDER</span></Button>
          </div>
        </Form>
      </div>
    </div>
  );
};